<template>
  <DatatableReportV3
    title="Job Costing"
    :source="source"
    :config="config"
    :filters="filters"
  />
</template>

<script setup lang="ts">
import { onMounted } from "vue";
import { useStore } from "vuex";

import { Filter } from "@/components/DatatableV3.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import JobCostEstimatedBadge from "@/Reports/JobCostEstimatedBadge.vue";
import JobCostServiceCategories from "@/Reports/JobCostServiceCategories.vue";
import JobCostTechnicians from "@/Reports/JobCostTechnicians.vue";
import JobCostTotal from "@/Reports/JobCostTotal.vue";

withDefaults(
  defineProps<{
    source?: string;
    filters?: Filter[];
  }>(),
  {
    source: "",
    filters: () => []
  }
);

// `data` keys and sortable/searchable flags mirror JobCostsDatatable#view_columns.
const totalColumn = (
  data: string,
  title: string,
  responsivePriority: number
) => ({
  data,
  title,
  responsivePriority,
  searchable: false,
  aggregateTitle: title,
  className: "text-right",
  footerClassName: "text-right"
});

const config = {
  responsive: true,
  // No default date range, so the newest jobs come first.
  order: [[1, "desc"]],
  // Narrow header filters, tight padding and wrapping text keep every column on one screen.
  columns: [
    {
      data: "completed_date",
      title: "Completion Date",
      responsivePriority: 11,
      filterName: "invoice_finalized",
      searchable: false
    },
    {
      data: "job",
      title: "Job",
      responsivePriority: 2,
      aggregateTitle: "# of Jobs"
    },
    { data: "invoice", title: "Invoice", responsivePriority: 14 },
    {
      data: "customer",
      title: "Customer",
      responsivePriority: 1,
      // Names stay on one line here; Department and Service Category wrap instead.
      className: "wrap-when-responsive"
    },
    {
      data: "department",
      title: "Department",
      responsivePriority: 12,
      filterName: "department_id",
      className: "wrap-when-responsive !whitespace-normal"
    },
    {
      data: "service_category",
      title: "Service Category",
      responsivePriority: 13,
      orderable: false,
      searchable: false,
      className: "wrap-when-responsive !whitespace-normal"
    },
    {
      data: "technicians",
      title: "Technician(s)",
      responsivePriority: 7,
      filterName: "technician_id",
      orderable: false,
      searchable: false,
      className: "wrap-when-responsive !whitespace-normal"
    },
    totalColumn("revenue", "Revenue", 5),
    totalColumn("job_cost", "COGS", 6),
    totalColumn("job_profit", "Job Profit ($)", 4),
    totalColumn("margin_pct", "Margin (%)", 3)
  ],
  vueComponents: [
    { rootId: "job-cost-estimated-vue", component: JobCostEstimatedBadge },
    {
      rootId: "job-cost-service-categories-vue",
      component: JobCostServiceCategories
    },
    { rootId: "job-cost-technicians-vue", component: JobCostTechnicians },
    { rootId: "job-cost-total-vue", component: JobCostTotal }
  ]
};

const store = useStore();

onMounted(() => {
  store.dispatch("addBreadcrumb", { label: "Job Costing" });
});
</script>

<style lang="scss" scoped>
/* A closed header filter takes no width, so the columns fit on one screen until the filters open. */
:deep(.col-filter:not(.active)) {
  width: 0;
  overflow: hidden;
}
/* Tighter side padding than the 1rem default buys back the width eleven columns need. */
:deep(table.dataTable th:not(:first-of-type)),
:deep(table.dataTable td:not(:first-of-type)) {
  padding-left: 0.625rem !important;
  padding-right: 0.625rem !important;
}
</style>
